import React, { useEffect, useMemo, useState } from "react";
// The same calendar the client screen's empty appointment tab wears. One mark
// for both, because they are the same thing missing — nothing booked.
import { ReactComponent as NoAppointmentsMark } from "../../../assets/svg/No data images/appointment.svg";
import PageHeader from "../../../components/layout/PageHeader";
import {
  Button,
  EmptyState,
  PlusIcon,
  SkeletonCard,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import type { EmptyStateProps } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// Deep imports rather than the appointments barrel. Three of these five
// dialogs are not on it — they are opened from inside that feature and nowhere
// else — and the barrel pulls the whole client Appointment tab in behind
// whatever is taken from it. Same reasoning the rail uses for its two counts.
import CancelAppointmentModal from "../../appointments/components/CancelAppointmentModal";
import CompleteAppointmentModal from "../../appointments/components/CompleteAppointmentModal";
import MeetingModal from "../../appointments/components/MeetingModal";
import RescheduleAppointmentModal from "../../appointments/components/RescheduleAppointmentModal";
import ScheduleAppointmentModal from "../../appointments/components/ScheduleAppointmentModal";
import {
  appointmentShortDate,
  appointmentTime,
  attendeeName,
  dayFull,
} from "../../appointments/appointments";
import type {
  AppointmentAttendee,
  ClientAppointment,
  NewClientAppointment,
  RescheduleAppointment,
} from "../../appointments/types/appointments.types";
import TaskDayStrip from "../components/TaskDayStrip";
import TaskSummaryCards from "../components/TaskSummaryCards";
import TaskTimeline from "../components/TaskTimeline";
import { useDayTasks } from "../hooks/useDayTasks";
import { useEverBooked } from "../hooks/useEverBooked";
import {
  atMidnight,
  isPastDay,
  isSameDay,
  nextUp,
  taskStateOf,
  taskSummary,
} from "../myTask";
import type { TaskState } from "../myTask";

/** The one thing a dialog is open about, and which dialog it is. */
type Pending =
  | { kind: "create" }
  | { kind: "meeting"; appointment: ClientAppointment }
  | { kind: "reschedule"; appointment: ClientAppointment }
  | { kind: "cancel"; appointment: ClientAppointment }
  | { kind: "complete"; appointment: ClientAppointment };

/**
 * The first name out of whatever the appointment called them — client or
 * enquiry, since both dialogs that read this ask the same question about an
 * hour and neither cares which kind of person it was with.
 */
const firstNameOf = (appointment: ClientAppointment): string =>
  (attendeeName(appointment) || "").trim().split(/\s+/)[0] || "your client";

/**
 * My task: one day of the practice, read as it will be lived.
 *
 * The week across the top, four figures under it, and the day itself as a
 * thread of appointments with the hour against each one. It is the only screen
 * in the app that is about a day rather than about a record — My clients is a
 * roster, Revenue is a ledger, a client's Appointment tab is one person's
 * history, and none of them answers "what am I doing this afternoon".
 *
 * The day comes off the ordinary appointments list, asked for as a window —
 * `GET /api/v1/appointments/?from=&to=`, local midnight to local midnight. The
 * booking dialogs ask the same endpoint the same way and get a different
 * answer: theirs is filtered down to what a new slot could collide with, and
 * this is the day as it stands, cancellations and completed sessions and all.
 * A day's diary has to account for the hours that did not go to plan as much
 * as the ones that did — see `fetchDayDiary` beside `fetchDayAppointments`.
 *
 * No `status` or `classification` on the request, though the endpoint offers
 * both. `status` takes one constant where this wants every one of them, and
 * `classification` is the server reading its own clock when it answered — on a
 * screen somebody leaves open all morning, "upcoming" has to keep meaning
 * upcoming as the morning passes, so the clock is read here instead.
 *
 * Everything the screen can do to an appointment is the appointments feature's
 * own: the same four dialogs the client screen opens, against the same four
 * endpoints. What is new here is the fifth — the Meeting dialog, which reads
 * one appointment back by its `public_id` — and the client picker on the
 * booking dialog, which this screen needs because a day does not imply a
 * person the way a client's record does.
 */
const MyTaskPage: React.FC = () => {
  const toast = useToast();
  const gate = useSubscriptionGate();

  /**
   * The day being looked at. Opens on today, which is what somebody coming to
   * this screen is nearly always asking about.
   *
   * Held at midnight so that changing the day is only ever a change of day:
   * the value is a cache key and a query param, and one carrying the hour it
   * happened to be created at would make every render a different day.
   */
  const [day, setDay] = useState(() => atMidnight(new Date()));

  /**
   * One `now` for the whole screen, so the thread, the tiles and the rows
   * cannot disagree about which appointment is happening.
   *
   * It has to move on its own: a call at one o'clock has to take the ring off
   * the twelve-thirty and start asking how that one went, without anybody
   * reloading — this is a screen somebody leaves open all morning. Every
   * minute, which is the resolution appointments are booked at.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const {
    appointments,
    status: loadStatus,
    error,
    reload,
    schedule,
    reschedule,
    cancel,
    complete,
  } = useDayTasks(day);

  // A day that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled. Stepping
  // along the strip goes through it too, so moving days shimmers rather than
  // showing yesterday's rows under today's date.
  const phase = useLoadPhase(loadStatus === "loading");

  const [pending, setPending] = useState<Pending | null>(null);
  // Set while a write is with the server, so a dialog's action spins rather
  // than taking a second press and booking the appointment twice.
  const [saving, setSaving] = useState(false);

  const summary = useMemo(
    () => taskSummary(appointments, now),
    [appointments, now]
  );

  const empty = appointments.length === 0;
  const today = isSameDay(day, now);
  // Behind us. Nothing can be booked into it — the dialogs refuse a date that
  // has gone — so an empty one of these is a fact rather than an invitation.
  const past = isPastDay(day, now);

  /**
   * Whether this practice has ever booked anything, asked only when it
   * matters: the day in hand is empty, it is one that can still be filled, and
   * the server has finished saying so.
   *
   * Three conditions and each one rules out a request that would be wasted. A
   * day with rows on it has answered the question by existing. A day still
   * loading has not asked one yet. And a day that has gone says the same two
   * lines whichever way the answer falls — nothing can be booked into last
   * Monday, so there is no first-run panel to decide about.
   *
   * Which leaves the ordinary case, a practitioner opening a Tuesday with four
   * appointments on it, sending nothing at all.
   */
  const everBooked = useEverBooked({
    enabled: empty && !past && loadStatus === "ready",
  });

  /**
   * Every row's state, worked out in one pass.
   *
   * In one pass because "next" is a fact about the day rather than about a
   * row — only one appointment can hold it, and a row cannot tell on its own
   * whether it is that one. Working it out per row would be the same scan of
   * the whole list once per row, and would let two rows both decide they were
   * next on a render where the clock moved between them.
   */
  const states = useMemo(() => {
    const nextId = nextUp(appointments, now)?.id;
    return new Map<string, TaskState>(
      appointments.map((appointment) => [
        appointment.id,
        taskStateOf(appointment, now, nextId),
      ])
    );
  }, [appointments, now]);

  /** Every write fails the same way, and the dialog stays up to be tried again. */
  const report = (title: string, cause: unknown) => {
    toast.error(
      title,
      cause instanceof ApiError
        ? cause.message
        : "We could not reach the server. Check your connection and try again."
    );
    console.error(`${title.toLowerCase()}:`, cause);
  };

  const handleCreate = async (
    input: NewClientAppointment,
    attendee?: AppointmentAttendee
  ) => {
    if (saving || !attendee) return;
    setSaving(true);

    try {
      const booked = await schedule(attendee, input);
      setPending(null);
      const who = attendeeName(booked);
      toast.success(
        "Appointment scheduled",
        `${appointmentShortDate(booked.startsAt)} at ${appointmentTime(
          booked.startsAt
        )}${who ? ` with ${who}` : ""}.`
      );
      // Booked onto a day that is not the one on screen — which the dialog
      // allows, since it takes any date from today on. Following it is the
      // only answer that does not leave somebody looking at a list their new
      // appointment is plainly not in.
      const landed = new Date(booked.startsAt);
      if (!Number.isNaN(landed.getTime()) && !isSameDay(landed, day)) {
        setDay(atMidnight(landed));
      }
    } catch (cause) {
      report("Could not schedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (input: RescheduleAppointment) => {
    if (saving || pending?.kind !== "reschedule") return;
    setSaving(true);

    try {
      const moved = await reschedule(pending.appointment.id, input);
      setPending(null);
      toast.success(
        "Appointment rescheduled",
        `Moved to ${appointmentShortDate(moved.startsAt)} at ${appointmentTime(
          moved.startsAt
        )}.`
      );
    } catch (cause) {
      report("Could not reschedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (saving || pending?.kind !== "cancel") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await cancel(appointment.id, reason);
      setPending(null);
      toast.success(
        "Appointment cancelled",
        `The ${appointmentTime(
          appointment.startsAt
        )} appointment with ${firstNameOf(appointment)} has been called off.`
      );
    } catch (cause) {
      report("Could not cancel this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (note: string) => {
    if (saving || pending?.kind !== "complete") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await complete(appointment.id, note);
      setPending(null);
      toast.success(
        "Appointment completed",
        `The ${appointmentTime(
          appointment.startsAt
        )} session with ${firstNameOf(appointment)} is on their record.`
      );
    } catch (cause) {
      report("Could not mark this appointment completed", cause);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Where the empty card is with what it needs, which is two answers rather
   * than one — the day, and then whether this practice is new.
   *
   * Folded into one phase so the card never says the wrong thing on its way to
   * the right one. The second question is only asked once the first has come
   * back empty, so there is a beat between them, and a card that drew the
   * compact lines in that beat would swap them for the first-run panel in
   * front of the one reader the panel is for. Holding the space blank until
   * both have landed is the honest version: we do not know yet.
   *
   * It goes through `useLoadPhase` for the usual reason — the probe answers
   * inside the delay nearly every time, so the shimmer is never reached and
   * the card goes straight from blank to filled.
   */
  const firstRunPhase = useLoadPhase(
    empty && !past && loadStatus === "ready" && everBooked === "unknown"
  );
  const emptyPhase = phase === "ready" ? firstRunPhase : phase;

  /**
   * What the day says when it has nothing in it.
   *
   * Four answers, and the order they are tried in is the whole of it.
   *
   * A failure comes first: an empty day under "nothing planned" would read as
   * an answer, when nothing was ever heard back — and it keeps the compact
   * layout, because a welcome mat under an error reads as though the error
   * were the screen anybody meant to land on.
   *
   * Then a practice that has never booked anything, on today or a day ahead.
   * That is the one case the welcome layout is for and the only one it gets:
   * a screen nothing has ever been put into has a job the compact lines
   * cannot do — say what the screen is for before asking anybody to fill it.
   * It is the first thing a new workspace sees here, so it is worth the room.
   *
   * Everything else is compact, and for one reason rather than two: a free
   * day in a working practice is a list reporting a result, and the reader
   * already knows what this screen is for because they have used it. Dressing
   * up an ordinary free Thursday as a first run is noise over a fact they can
   * see from the four zeroes above it, and the Create button on the strip is
   * one line up from the words either way. A day that has gone is the same
   * state with the tense moved: nothing can be put into last Monday — both
   * booking dialogs refuse a date behind today — so it says what was true of
   * it and points at the days that can still be filled.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState(
        "We could not load this day",
        <Button variant="outline" size="sm" onClick={reload}>
          Try again
        </Button>
      );
      return error ? { ...state, description: error } : state;
    }

    if (past) {
      return {
        title: "Nothing was planned for this day",
        description: `You had no appointments on ${dayFull(
          day
        )}. Pick a day from today onwards to plan one.`,
      };
    }

    if (everBooked === "never") {
      return {
        variant: "welcome",
        illustration: <NoAppointmentsMark aria-hidden className="h-14 w-14" />,
        title: today
          ? "Nothing planned for today"
          : "Nothing planned for this day",
        description: `You have nothing on ${dayFull(
          day
        )}. Create a task to plan your day.`,
        highlights: {
          title: "Here you can",
          items: [
            "Create and plan your tasks for the day.",
            "Keep track of your upcoming and completed tasks.",
          ],
        },
        action: (
          <Button
            size="md"
            startIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => setPending({ kind: "create" })}
            {...gate.props("schedule an appointment")}
          >
            Create task
          </Button>
        ),
      };
    }

    // A free day in a practice that is already running. The same two lines the
    // day behind us gets, in the tense this one is in — "had no appointments"
    // over a Friday still to come is the wrong sentence about a day that has
    // not happened yet.
    return {
      title: today ? "Nothing planned for today" : "Nothing planned for this day",
      description: `You have nothing on ${dayFull(
        day
      )}. Create a task to plan your day.`,
    };
  };

  return (
    // The page never scrolls; only the day inside it does.
    <div className="flex h-full flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
      {/* No actions on the heading: Create rides the day strip below, which is
          where the design puts it and where the day it would be booked on is
          already being chosen. */}
      <PageHeader title="My task" />

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: this line is not seen, so it costs nothing to be early, and
          the blocks it stands for are hidden from screen readers — they are the
          shape of an answer rather than the answer. */}
      {loadStatus === "loading" && (
        <p role="status" className="sr-only">
          Loading this day’s appointments…
        </p>
      )}

      <div className="mt-4 flex-shrink-0 3xl:mt-5">
        <TaskDayStrip
          selected={day}
          onSelect={(picked) => setDay(picked)}
          action={
            <Button
              size="md"
              startIcon={<PlusIcon className="h-4 w-4" />}
              onClick={() => setPending({ kind: "create" })}
              {...gate.props("schedule an appointment")}
            >
              Create
            </Button>
          }
        />
      </div>

      <div className="mt-4 flex-shrink-0 3xl:mt-5">
        <TaskSummaryCards
          summary={summary}
          phase={phase}
          failed={loadStatus === "failed"}
        />
      </div>

      {emptyPhase !== "ready" && empty ? (
        // One block the height of the day, rather than a column of blocks in
        // the shape of rows nobody knows the number of yet.
        <div className="mt-4 min-h-0 flex-1 3xl:mt-5">
          <SkeletonCard
            phase={emptyPhase}
            className="h-full w-full rounded-md"
          />
        </div>
      ) : empty ? (
        <section className="mt-4 flex min-h-0 flex-1 flex-col overflow-auto rounded-md border border-line bg-white 3xl:mt-5">
          {/* Centred down the card rather than sitting at the top of it. The
              card stretches to the foot of the page, so a block pinned to its
              top edge leaves a third of the screen blank underneath and reads
              as a list that failed to finish drawing.

              `my-auto` rather than `justify-center` on the card: this card
              scrolls, and centring a scroll container with `justify-content`
              puts the overflow at the top out of reach — on a short window
              the illustration would be cut off with no way to scroll up to it.
              Equal auto margins centre the same way and still let it scroll.

              Down the card only, and `my` rather than `m` for that reason: an
              auto margin across would stop the block stretching and pull it
              into the middle, and it has to stay set to the left. The
              highlights under the description hang off a rule, and a rule
              needs a left edge to hang from — see `EmptyState`. */}
          <EmptyState {...emptyState()} className="my-auto" />
        </section>
      ) : (
        // No card around the rows. The thread down the left is the spine of
        // this screen and a border beside it would read as a second one — and
        // the rows are already cards. Only this scrolls.
        //
        // The padding and the negative margin are one thing, not two: the
        // current row carries a soft shadow, and a scroller clips at its own
        // edge, so without a margin of air inside it the shadow is sliced off
        // down the right-hand side. Equal negative margin outside puts the
        // rows back exactly under the tiles above them, so the air costs the
        // layout nothing.
        <section
          aria-label={`Appointments on ${dayFull(day)}`}
          className="custom-scrollbar-overlay -mx-2 mt-4 min-h-0 flex-1 overflow-auto px-2 py-1 3xl:mt-5"
        >
          <TaskTimeline
            appointments={appointments}
            states={states}
            now={now}
            gate={gate.props("mark an appointment")}
            onOpen={(appointment) =>
              setPending({ kind: "meeting", appointment })
            }
            onComplete={(appointment) =>
              setPending({ kind: "complete", appointment })
            }
            onCancel={(appointment) =>
              setPending({ kind: "cancel", appointment })
            }
          />
        </section>
      )}

      {/* Only ever one of the five. Each stays up while its write is with the
          server, so a booking that could not be made leaves the details on
          screen to send again rather than closing over a failure. */}
      {pending?.kind === "create" && (
        <ScheduleAppointmentModal
          pickClient
          title="Create meeting"
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleCreate}
        />
      )}

      {pending?.kind === "meeting" && (
        <MeetingModal
          appointment={pending.appointment}
          gate={gate.props("change an appointment")}
          onClose={() => setPending(null)}
          // The Meeting dialog hands straight over to the one that acts, so
          // the details it was read in are not sitting behind a second panel
          // saying the same thing.
          onReschedule={(appointment) =>
            setPending({ kind: "reschedule", appointment })
          }
          onCancel={(appointment) =>
            setPending({ kind: "cancel", appointment })
          }
        />
      )}

      {pending?.kind === "reschedule" && (
        <RescheduleAppointmentModal
          appointment={pending.appointment}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleReschedule}
        />
      )}

      {pending?.kind === "complete" && (
        <CompleteAppointmentModal
          appointment={pending.appointment}
          firstName={firstNameOf(pending.appointment)}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleComplete}
        />
      )}

      {pending?.kind === "cancel" && (
        <CancelAppointmentModal
          appointment={pending.appointment}
          firstName={firstNameOf(pending.appointment)}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
};

export default MyTaskPage;
